import { useState } from "react";
import { ChevronUp, LayoutGrid, MessageCircle, Phone, Sparkles, User } from "lucide-react";
import { cn } from "@/lib/utils";

const soonButtonClass =
  "inline-flex cursor-not-allowed items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-semibold tracking-wide text-gray-400 opacity-70";

const soonMenuItemClass =
  "flex w-full cursor-not-allowed items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-xs font-semibold tracking-wide text-gray-500 opacity-80 dark:text-gray-400";

const SOON_ACTIONS = [
  { label: "Apps Soon", ariaLabel: "Apps, Coming Soon", Icon: LayoutGrid },
  { label: "Chat Soon", ariaLabel: "Chat, Coming Soon", Icon: MessageCircle },
  { label: "Contacts Soon", ariaLabel: "Contacts, Coming Soon", Icon: User },
  { label: "Calls Soon", ariaLabel: "Calls, Coming Soon", Icon: Phone },
] as const;

function SoonActionsInline() {
  return (
    <>
      {SOON_ACTIONS.map(({ label, ariaLabel, Icon }) => (
        <button
          key={label}
          type="button"
          disabled
          className={soonButtonClass}
          aria-label={ariaLabel}
          title="Coming Soon"
        >
          <Icon className="size-[16px]" aria-hidden />
          {label}
        </button>
      ))}
    </>
  );
}

export function DirectoryUtilityBar() {
  const [soonOpen, setSoonOpen] = useState(false);

  return (
    <div
      role="toolbar"
      aria-label="Quick actions"
      className={cn(
        "fixed bottom-0 left-0 right-0 z-30 flex h-10 items-center justify-between border-t border-gray-300 bg-[#f1f3f4] px-2 sm:px-4 dark:border-white/10 dark:bg-zinc-900",
        "xl:left-[var(--directory-rail-w)]",
      )}
    >
      <div className="hidden items-center gap-1 md:flex">
        <SoonActionsInline />
      </div>

      <div className="relative md:hidden">
        {soonOpen ? (
          <div
            role="dialog"
            aria-label="Coming Soon Actions"
            className="absolute bottom-full left-0 z-40 mb-2 w-56 rounded-xl border border-gray-200 bg-[#f1f3f4] p-2 shadow-lg dark:border-white/10 dark:bg-zinc-900"
          >
            <p className="px-3 pb-1.5 pt-1 text-[10px] font-semibold tracking-wide text-gray-500 dark:text-gray-400">
              Coming Soon
            </p>
            <ul className="flex flex-col gap-0.5" role="list">
              {SOON_ACTIONS.map(({ label, ariaLabel, Icon }) => (
                <li key={label}>
                  <button
                    type="button"
                    disabled
                    className={soonMenuItemClass}
                    aria-label={ariaLabel}
                    title="Coming Soon"
                  >
                    <Icon className="size-4 shrink-0" aria-hidden />
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <button
          type="button"
          aria-expanded={soonOpen}
          aria-haspopup="dialog"
          aria-label={soonOpen ? "Close Coming Soon Actions" : "Open Coming Soon Actions"}
          onClick={() => setSoonOpen((open) => !open)}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 shadow-sm dark:border-white/15 dark:bg-zinc-800 dark:text-gray-200",
            soonOpen && "bg-gray-100 dark:bg-zinc-700",
          )}
        >
          <LayoutGrid className="size-3.5 shrink-0" aria-hidden />
          More
          <ChevronUp
            className={cn("size-3.5 shrink-0 transition-transform", soonOpen && "rotate-180")}
            aria-hidden
          />
        </button>
      </div>

      <a
        href="https://ai.atw.net"
        className="inline-flex items-center gap-1.5 rounded-md border border-emerald-800/30 bg-white px-2.5 py-1 text-xs font-semibold text-[#0b3d2e] shadow-sm hover:bg-emerald-50 dark:border-emerald-500/40 dark:bg-zinc-800 dark:text-emerald-200 dark:hover:bg-zinc-700"
      >
        <Sparkles className="size-3.5 shrink-0" aria-hidden />
        Ask One AI
      </a>
    </div>
  );
}
